import React, { useState } from "react";
import { Checkbox, Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import {
  type ConnectionEditState,
  type ConnectionEditStateUpdater,
  type Status,
  type SourceStatusMapping,
} from "@onetasklist/shared";
import {
  useMondaySourceSettingsText,
  useMondaySourceSettingsViewModel,
} from "@onetasklist/shared/sources/monday";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";
import { StatusMappingSection } from "./components/StatusMappingSection";

interface MondaySourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
  boardStatuses: Status[];
}

export const MondaySourceSettings: React.FC<MondaySourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
  boardStatuses,
}) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useMondaySourceSettingsText(t);
  const vm = useMondaySourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [boardQuery, setBoardQuery] = useState("");
  const [statusColumnQuery, setStatusColumnQuery] = useState("");
  const [statusesQuery, setStatusesQuery] = useState("");
  const [doneStatusQuery, setDoneStatusQuery] = useState("");
  const [ownerColumnQuery, setOwnerColumnQuery] = useState("");
  const [dueDateColumnQuery, setDueDateColumnQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  if (vm.hasError) {
    return <Text>{text.loadErrorText}</Text>;
  }

  return (
    <>
      <SettingsSectionCard
        sectionOffset={sectionOffset}
        title={t(
          "board.connections.catalogue.sourceSettings.monday.sectionBoard",
        )}
      >
        <Field
          orientation="horizontal"
          label={text.boardLabelText}
          hint={text.boardHintText}
        >
          <SourceSingleTagPicker
            selectedId={
              vm.selectedBoardId === vm.noneOptionValue
                ? undefined
                : vm.selectedBoardId
            }
            selectedLabel={vm.selectedBoardName}
            query={boardQuery}
            placeholder={text.boardPlaceholderText}
            options={vm.boards.map((board) => ({
              id: board.id,
              label: board.name ?? board.id,
            }))}
            onQueryChange={setBoardQuery}
            onSelect={vm.onSelectBoard}
            clearSelectionText={text.boardPlaceholderText}
            isLoading={!vm.boardsLoaded}
          />
        </Field>
      </SettingsSectionCard>

      {vm.selectedBoardId !== vm.noneOptionValue && (
        <SettingsSectionCard
          sectionOffset={sectionOffset + 1}
          title={t(
            "board.connections.catalogue.sourceSettings.monday.sectionStatuses",
          )}
        >
          <Field
            orientation="horizontal"
            label={text.statusColumnLabelText}
            hint={text.statusColumnHintText}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedStatusColumnId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedStatusColumnId
              }
              selectedLabel={vm.selectedStatusColumnTitle}
              query={statusColumnQuery}
              placeholder={text.statusColumnPlaceholderText}
              options={vm.statusColumns.map((column) => ({
                id: column.id,
                label: column.title ?? column.id,
              }))}
              onQueryChange={setStatusColumnQuery}
              onSelect={vm.onSelectStatusColumn}
              clearSelectionText={text.statusColumnPlaceholderText}
              isLoading={!vm.boardsLoaded}
            />
          </Field>

          {vm.selectedStatusColumnId !== vm.noneOptionValue && (
            <>
              <Field
                orientation="horizontal"
                label={text.statusesLabelText}
                hint={text.statusesHintText}
              >
                <SourceMultiTagPicker
                  selectedIds={vm.selectedStatuses}
                  query={statusesQuery}
                  placeholder={text.statusesPlaceholderText}
                  options={vm.statuses.map((status) => ({
                    id: status,
                    label: status,
                  }))}
                  onQueryChange={setStatusesQuery}
                  onSelectionChange={vm.onSelectStatuses}
                  minQueryLength={0}
                  typeToSearchText={text.statusesHintText}
                  isLoading={!vm.boardsLoaded}
                />
              </Field>

              <Field
                orientation="horizontal"
                label={text.doneStatusLabelText}
                hint={text.doneStatusHintText}
              >
                <SourceSingleTagPicker
                  selectedId={
                    vm.selectedDoneStatus === vm.noneOptionValue
                      ? undefined
                      : vm.selectedDoneStatus
                  }
                  selectedLabel={
                    vm.selectedDoneStatus === vm.noneOptionValue
                      ? undefined
                      : vm.selectedDoneStatus
                  }
                  query={doneStatusQuery}
                  placeholder={text.doneStatusPlaceholderText}
                  options={vm.statuses.map((status) => ({
                    id: status,
                    label: status,
                  }))}
                  onQueryChange={setDoneStatusQuery}
                  onSelect={vm.onSelectDoneStatus}
                  clearSelectionText={text.doneStatusPlaceholderText}
                  isLoading={!vm.boardsLoaded}
                />
              </Field>
            </>
          )}
        </SettingsSectionCard>
      )}

      {vm.selectedBoardId !== vm.noneOptionValue &&
        (vm.peopleColumns.length > 0 || vm.dateColumns.length > 0) && (
          <SettingsSectionCard
            sectionOffset={sectionOffset + 2}
            title={t(
              "board.connections.catalogue.sourceSettings.monday.sectionOwner",
            )}
          >
            {vm.peopleColumns.length > 0 && (
              <Field
                orientation="horizontal"
                label={text.ownerColumnLabelText}
                hint={text.ownerColumnHintText}
              >
                <SourceSingleTagPicker
                  selectedId={
                    vm.selectedOwnerColumnId === vm.noneOptionValue
                      ? undefined
                      : vm.selectedOwnerColumnId
                  }
                  selectedLabel={vm.selectedOwnerColumnTitle}
                  query={ownerColumnQuery}
                  placeholder={text.ownerColumnPlaceholderText}
                  options={vm.peopleColumns.map((column) => ({
                    id: column.id,
                    label: column.title ?? column.id,
                  }))}
                  onQueryChange={setOwnerColumnQuery}
                  onSelect={vm.onSelectOwnerColumn}
                  clearSelectionText={text.ownerColumnPlaceholderText}
                  isLoading={!vm.boardsLoaded}
                />
              </Field>
            )}

            {vm.selectedOwnerColumnId !== vm.noneOptionValue && (
              <Field orientation="horizontal" label={text.assignedToMeLabelText}>
                <Checkbox
                  checked={vm.assignedToMe}
                  onChange={(_, data) =>
                    vm.onToggleAssignedToMe(Boolean(data.checked))
                  }
                />
              </Field>
            )}

            {vm.dateColumns.length > 0 && (
              <Field
                orientation="horizontal"
                label={text.dueDateColumnLabelText}
                hint={text.dueDateColumnHintText}
              >
                <SourceSingleTagPicker
                  selectedId={
                    vm.selectedDueDateColumnId === vm.noneOptionValue
                      ? undefined
                      : vm.selectedDueDateColumnId
                  }
                  selectedLabel={vm.selectedDueDateColumnTitle}
                  query={dueDateColumnQuery}
                  placeholder={text.dueDateColumnPlaceholderText}
                  options={vm.dateColumns.map((column) => ({
                    id: column.id,
                    label: column.title ?? column.id,
                  }))}
                  onQueryChange={setDueDateColumnQuery}
                  onSelect={vm.onSelectDueDateColumn}
                  clearSelectionText={text.dueDateColumnPlaceholderText}
                  isLoading={!vm.boardsLoaded}
                />
              </Field>
            )}
          </SettingsSectionCard>
        )}

      {vm.selectedBoardId !== vm.noneOptionValue &&
        vm.selectedStatuses.length > 0 && (
          <StatusMappingSection
            sectionNumber={sectionOffset + 3}
            items={vm.selectedStatuses.map((s) => ({ id: s, name: s }))}
            mappings={
              ((editingConnection.configuration as Record<string, unknown>)
                ?.statusMappings as SourceStatusMapping[] | undefined) ?? []
            }
            boardStatuses={boardStatuses}
            onChange={(mappings) =>
              onUpdate({
                configuration: {
                  ...(editingConnection.configuration as Record<string, unknown>),
                  statusMappings: mappings,
                },
              })
            }
          />
        )}
    </>
  );
};
